import * as THREE from 'three'
import type {
  AnimationBindingResolver,
  AnimationBindingTarget,
  AnimationPropertySample,
  AnimationTargetRef,
  CameraAnimationTargetRef,
  CameraBindingHost,
  CameraStatePatch,
} from '@/shared/types/animation'

export const CAMERA_ANIMATION_PROPERTIES = [
  'position',
  'target',
  'up',
  'fov',
  'zoom',
  'roll',
] as const

export type CameraAnimationProperty = (typeof CAMERA_ANIMATION_PROPERTIES)[number]

export function createCameraBindingResolver(host: CameraBindingHost): AnimationBindingResolver {
  return {
    resolve(targetRef: AnimationTargetRef): AnimationBindingTarget | null {
      if (targetRef.kind !== 'camera') {
        return null
      }

      const resolved = host.resolveCameraTarget(targetRef as CameraAnimationTargetRef)
      if (!resolved) {
        return null
      }

      return {
        key: resolved.key,
        targetRef: {
          kind: 'camera',
          viewId: resolved.viewId,
        },
        supportedProperties: CAMERA_ANIMATION_PROPERTIES,
        write(samples) {
          const patch = cameraSamplesToPatch(samples)
          host.applyCameraStatePatch(resolved.viewId, patch, {
            cancelTransition: true,
          })
        },
      }
    },
  }
}

function cameraSamplesToPatch(samples: readonly AnimationPropertySample[]): CameraStatePatch {
  const patch: CameraStatePatch = {}
  for (const sample of samples) {
    switch (sample.propertyPath as CameraAnimationProperty) {
      case 'position':
        patch.position = toVector3(sample.value)
        break
      case 'target':
        patch.target = toVector3(sample.value)
        break
      case 'up':
        patch.up = toVector3(sample.value)
        break
      case 'fov':
        patch.fov = sample.value[0]
        break
      case 'zoom':
        patch.zoom = sample.value[0]
        break
      case 'roll':
        patch.roll = sample.value[0]
        break
    }
  }
  return patch
}

function toVector3(value: readonly number[]): THREE.Vector3 {
  return new THREE.Vector3(value[0] ?? 0, value[1] ?? 0, value[2] ?? 0)
}
